import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { RiArrowRightSLine } from 'react-icons/ri';
import { isItemActive as checkItemActive } from './sidebarUtils';

const SidebarSubmenu = ({ item, isOpen, location, level = 1 }) => {
  const pathname = location?.pathname ?? '';
  const [openNestedSubmenu, setOpenNestedSubmenu] = useState(null);

  // Auto-open the nested submenu that contains the current path
  useEffect(() => {
    if (!item?.children) return;
    const index = item.children.findIndex(
      (child) => checkItemActive(child, pathname)
    );
    if (index >= 0) setOpenNestedSubmenu(index);
  }, [pathname, item?.children]);

  const toggleNestedSubmenu = (childIndex) => {
    if (openNestedSubmenu === childIndex) {
      setOpenNestedSubmenu(null);
    } else {
      setOpenNestedSubmenu(childIndex);
    }
  };

  const hasNestedChildren = (child) => {
    return child.children && child.children.length > 0;
  };

  const isThirdLevel = level >= 2;
  const containerPl = isThirdLevel ? 'pl-4' : 'pl-6';

  return (
    <div className={`relative mt-2 ${level === 1 ? 'ml-5' : 'ml-2'} space-y-1 pb-1`}>
      {/* Vertical line */}
      <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-theme-border-secondary rounded-full"></div>
      
      <div className={containerPl}>
        {item.children.map((child, childIndex) => {
          const isActive = checkItemActive(child, pathname);
          const hasChildren = hasNestedChildren(child);
          const isNestedOpen = openNestedSubmenu === childIndex;

          // If child has nested children, render as expandable button
          if (hasChildren) {
            const padClass = isThirdLevel ? 'pl-1 pr-2' : 'pl-1.5 pr-3';
            const showIcon = !isThirdLevel;
            return (
              <div key={childIndex} className="mb-1">
                <button
                  onClick={() => toggleNestedSubmenu(childIndex)}
                  className={`w-full flex items-center justify-between ${padClass} py-2 rounded-md text-sm group/child relative ${
                    isActive
                      ? 'text-primary bg-primary-muted font-medium'
                      : 'text-theme-text-secondary hover:text-primary hover:bg-theme-bg-secondary'
                  }`}
                >
                  {/* Horizontal connector */}
                  <div className="absolute left-0 top-1/2 w-3 h-0.5 bg-theme-border-secondary -translate-x-3 -translate-y-1/2"></div>
                  
                  <div className="flex items-center flex-1 min-w-0">
                    {showIcon && child.icon && (
                      <child.icon className={`w-4 h-4 mr-2 shrink-0 ${isActive ? 'text-primary' : 'group-hover/child:text-primary'}`} />
                    )}
                    <span className={`min-w-0 ${isThirdLevel ? 'break-words line-clamp-2' : 'truncate'} ${isActive ? 'text-primary' : 'group-hover/child:text-primary'}`}>{child.label}</span>
                  </div>
                  
                  <RiArrowRightSLine 
                    className={`w-3 h-3 transition-transform shrink-0 ${isThirdLevel ? 'ml-1' : 'ml-2'} ${isNestedOpen ? 'transform rotate-90' : ''}`} 
                  />
                </button>

                {/* Recursively render nested children (3rd level) */}
                {isNestedOpen && (
                  <SidebarSubmenu 
                    item={child} 
                    isOpen={isNestedOpen} 
                    location={location} 
                    level={level + 1}
                  />
                )}
              </div>
            );
          }

          // Regular link without nested children (2nd or 3rd level)
          const linkPadClass = isThirdLevel ? 'pl-1 pr-2' : 'pl-1.5 pr-3';
          const showLinkIcon = !isThirdLevel;
          return (
            <Link
              key={childIndex}
              to={child.path || '#'}
              className={`flex items-center min-w-0 ${linkPadClass} py-2 rounded-md text-sm group/child relative ${
                isActive
                  ? 'text-primary bg-primary-muted font-medium'
                  : 'text-theme-text-secondary hover:text-primary hover:bg-theme-bg-secondary'
              }`}
            >
              {/* Horizontal connector */}
              <div className="absolute left-0 top-1/2 w-3 h-0.5 bg-theme-border-secondary -translate-x-3 -translate-y-1/2"></div>
              
              {showLinkIcon && child.icon && (
                <child.icon className={`w-4 h-4 mr-2 shrink-0 ${isActive ? 'text-primary' : 'group-hover/child:text-primary'}`} />
              )}
              <span className={`min-w-0 ${isThirdLevel ? 'break-words line-clamp-2' : 'truncate'} ${isActive ? 'text-primary' : 'group-hover/child:text-primary'}`}>{child.label}</span>
            </Link>
          );
        })}
      </div>
    </div>
  );
};

export default SidebarSubmenu;
